<script setup lang="ts">
    import BackButton from '@/components/BackButton.ce.vue';
    import { DESCRIPTIONS } from '@/constants/descriptions';
    import { type RequestType, REQUEST_TYPES } from '@/constants/request-types';
    import { SURVEY_APPOINTMENT, SURVEY_ORGANIZER, SURVEY_THEME } from '@/constants/survey';
    import { WIDGET_MODULES } from '@/constants/tracking';
    import formKitConfig from '@/formkit';
    import { formatCurrencyNoCent } from '@/helpers/formatting';
    import { extractRequestId } from '@/helpers/tracking';
    import { type ProgrameeTyping } from '@/typings/widget';
    import { type FormKitNode } from '@formkit/core';
    import { FormKitMessages, FormKitProvider, FormKitRoot, defaultConfig } from '@formkit/vue';
    import DatePicker from '@shared/components/form/date-picker.vue';
    import Email from '@shared/components/form/email.vue';
    import NumberInput from '@shared/components/form/number-input.vue';
    import PhoneNumber from '@shared/components/form/phone-number.vue';
    import TextInput from '@shared/components/form/text-input.vue';
    import Textarea from '@shared/components/form/textarea.vue';
    import { ConstantsDateTime } from '@shared/constants';
    import { DateTime } from '@shared/helpers';
    import { dayjsObject } from '@shared/helpers/date-time';
    import { buildNewNotes } from '@shared/helpers/notes';
    import { type IdTyping } from '@shared/typings';
    import axios from 'axios';

    const props = defineProps<{
        venue: string
        packageId: IdTyping | string
        locale: string
    }>();

    const emit = defineEmits<{(event: 'processRequest', type: unknown), (event: 'scrollToTop' | 'goBack')}>();

    const { t } = useI18n();

    // Stores
    const {
        bookerName,
        selectedDate,
        guestAmount,
        surveyOrganizer,
        surveyTheme,
        descriptionIds,
        budgetAmount,
        appointmentDate,
        surveyAppointment,
        eventTypeId: pathEventTypeId,
    } = storeToRefs(useWidgetPathStore());
    const {
        venue: venueData,
        venueEventTypes,
        venueProducts,
        venueSpaces,
    } = storeToRefs(useWidgetVenueStore());
    const trackingStore = useWidgetTrackingStore();
    const {
        spaceIds,
        productIds,
        selectedDate: businessSelectedDate,
        selectedTimeRange,
        guestAmount: businessGuestAmount,
    } = storeToRefs(useWidgetBusinessStore());
    const {
        eventTypeFilter,
        guestAmountFilter,
        startDateFilter,
        startTimeFilter,
    } = storeToRefs(useWidgetFilterStore());

    // Refs
    const formData: Ref<{
        eventDate: string | null
        guestAmount: number | string | null
        name: string | null
        surname: string | null
        companyName: string | null
        email: string | null
        phoneCountryPrefix: string | null
        phoneNumber: string | null
        message: string
    }> = ref({
        // Filter values are the first source of truth, falling back to the business/path stores
        eventDate: startDateFilter.value || businessSelectedDate.value || selectedDate.value || null,
        guestAmount: (guestAmountFilter.value > 0 ? guestAmountFilter.value : null) ?? businessGuestAmount.value ?? guestAmount.value ?? null,
        name: bookerName.value ?? null,
        surname: null,
        companyName: null,
        email: null,
        phoneCountryPrefix: '+31',
        phoneNumber: null,
        message: '',
    });

    // Computed
    const organizationType = computed(() => t(SURVEY_ORGANIZER.find(org => org.value === surveyOrganizer.value)?.label ?? ''));
    const theme = computed(() => t(SURVEY_THEME.find(thm => thm.value === surveyTheme.value)?.label ?? ''));
    const flexibility = computed(() => t(SURVEY_APPOINTMENT.find(app => app.value === surveyAppointment.value)?.label ?? ''));
    const eventTypeId = computed(() => {
        const preselectedEventTypeId = Number(eventTypeFilter.value);
        const selectedPackage = venueData.value?.packages.find(pkg => pkg.id === props.packageId);

        if (preselectedEventTypeId) {
            return selectedPackage?.eventTypes
                ?.find(eventType => eventType.id === preselectedEventTypeId)?.id;
        }

        return selectedPackage?.eventTypes?.[0]?.id;
    });
    const descriptions = computed(() =>
        descriptionIds.value
            .map(id => t(DESCRIPTIONS.find(desc => desc.id === id)?.name ?? ''))
            .join(', ') ?? '');
    const budget = computed(() => formatCurrencyNoCent(
        Number(budgetAmount.value), 'EUR', props.locale,
    ));
    const appointment = computed(() => appointmentDate.value
        ? dayjsObject(appointmentDate.value)
            .format('DD MMMM, YYYY')
        : '');

    // Methods
    const getFlexibilityText = (): string => {
        const date = DateTime.dayjsObject(appointmentDate.value);

        const [
            weekday, day, month, year,
        ] = date.format('dddd D MMMM YYYY')
            .split(' ');

        const surveyAppointmentItems = useEditableClone(SURVEY_APPOINTMENT);
        surveyAppointmentItems[0].label = `${t('widget.survey-appointment.date-only')} ${weekday} ${day} ${month} ${year}`;
        surveyAppointmentItems[1].label = `${t('widget.survey-appointment.date-some-1')} ${weekday} ${t('widget.survey-appointment.date-some-2')} ${month} ${year}`;

        const surveyText = surveyAppointmentItems.find(item => item.value === surveyAppointment.value) ?? {
            notranslate: false,
            label: '',
        };

        if (surveyText.notranslate) return surveyText.label;

        return t(surveyText.label);
    };

    // eslint-disable-next-line max-lines-per-function
    const buildProgram = (): ProgrameeTyping[] => {
        const transformedGenericTimeslot = {
            name: t('widget.global.labels.general'),
            notes: [],
        };

        const transformedProducts = productIds.value
            .filter(productId => productId && productId !== 0)
            .map(productId => ({
                productId,
                amount: 1,
                notes: buildNewNotes(venueProducts.value.find(product => product.id === productId)?.notes),
            }));

        const transformedSpaces = spaceIds.value
            .filter(spaceId => spaceId && spaceId !== 0)
            .map(spaceId => ({
                spaceId,
                amount: 1,
                notes: buildNewNotes(venueSpaces.value.find(space => space.id === spaceId)?.notes),
            }));

        return [
            {
                name: 'Day 1',
                date: DateTime.dayjsObject(businessSelectedDate.value)
                    .format(ConstantsDateTime.dateFormatCalendar),
                genericTimeslot: transformedGenericTimeslot,
                timeslots: [
                    {
                        name: `${t('widget.global.labels.timeslot')}-1`,
                        notes: [],
                        startDateTime: DateTime.dayjsObject(`${businessSelectedDate.value} ${selectedTimeRange.value.min}`)
                            .format(ConstantsDateTime.dateTimeFormatFormKit),
                        endDateTime: DateTime.dayjsObject(`${businessSelectedDate.value} ${selectedTimeRange.value.max}`)
                            .format(ConstantsDateTime.dateTimeFormatFormKit),
                        guestAmounts: [],
                        products: transformedProducts,
                        spaces: transformedSpaces,
                    },
                ],
            },
        ] as unknown as ProgrameeTyping[];
    };

    const messageBuilder = (): string => {
        if (organizationType.value || theme.value || flexibility.value || descriptions.value || appointment.value) {
            return `- Organization type: ${organizationType.value}\n\n- Theme: ${theme.value}\n\n- Flexibility: ${getFlexibilityText()}\n\n- Descriptions: ${descriptions.value}\n\n- Budget: ${budget.value}\n\n - Appointment Date: ${appointment.value}\n\n`;
        }
        return '';
    };

    // eslint-disable-next-line max-lines-per-function
    const processForm = async(submittedData: typeof formData.value, formNode: FormKitNode): Promise<void> => {
        const {
            name,
            surname,
            eventDate,
            companyName,
            email,
            phoneNumber,
            phoneCountryPrefix,
            guestAmount: guestAmountForm,
        } = submittedData;

        const eventTypeIdValue = pathEventTypeId.value > 0 ? pathEventTypeId.value : Number(eventTypeId.value);

        const payload = {
            eventStartDate: eventDate,
            eventStartTime: '',
            event: {
                name: `Widget Package Quote - ${name} ${surname}`,
                mainBooker: {
                    email,
                    userName: `${name} ${surname}`,
                    phoneCountryPrefix,
                    phoneNumber,
                    company: { name: companyName },
                },
                eventTypeId: eventTypeIdValue,
            },
            venuePackageId: props.packageId,
            requestType: REQUEST_TYPES.package as RequestType,
            guestAmount: Number(guestAmountForm),
            programme: [] as ProgrameeTyping[],
            message: messageBuilder() + submittedData.message,
        };

        if (props.packageId && startTimeFilter.value) {
            payload.eventStartTime = DateTime.dayjsFormatter(startTimeFilter.value, 'HH:mm')
                .format(ConstantsDateTime.dateTimeHoursFormatFull);
        }

        if (!props.packageId) {
            payload.event.name = `Widget Business Package Quote - ${name} ${surname}`;
            payload.requestType = REQUEST_TYPES.businessPackage;
            payload.programme = buildProgram();
        }

        try {
            const response = await axios
                .post(
                    `${import.meta.env.VITE_API_BASE_URL}/api/v1/public/quote/create`, payload, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': props.locale,
                            'X-Venue-Id': props.venue,
                        },
                    },
                );
            emit('processRequest', response.data);

            /*
             * Analytics: fire only after the backend confirms (HTTP 2xx). PackageForm is used in
             * both the standalone packages flow and the in-journey package branch, so attribute to
             * whichever module is currently active.
             */
            trackingStore.trackRequestSubmitted({
                module: trackingStore.activeModule ?? WIDGET_MODULES.PACKAGES,
                requestId: extractRequestId(response.data),
                eventType: venueEventTypes.value.find(type => Number(type.id) === Number(eventTypeIdValue))?.name ?? null,
                guestAmount: Number(guestAmountForm) || null,
                budget: budgetAmount.value || null,
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Posting request form failed: ', error);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const msg = t('widget.messages.errors.request-failed');
            formNode.setErrors([msg]);
        }
    };

    onMounted(() => {
        emit('scrollToTop');
    });
</script>

<template>
    <div class="package-form">
        <div class="package-form__back-btn">
            <BackButton
                :on-click="() => emit('goBack')"
            />
        </div>
        <div class="package-form__form">
            <div class="package-form__form-header">
                <div class="package-form__form-title">
                    <h1>{{ t('widget.titles.sent-us-request') }}</h1>
                </div>
                <div class="package-form__form-description">
                    {{ t('widget.messages.sent-us-request') }}
                </div>
            </div>
            <div class="package-form__form-body">
                <FormKitProvider :config="defaultConfig(formKitConfig)">
                    <FormKitRoot>
                        <FormKit
                            id="request-form"
                            v-model="formData"
                            :incomplete-message="false"
                            :submit-label="t('widget.buttons.send')"
                            :submit-attrs="{
                                inputClass: 'btn btn--primary',
                                wrapperClass: 'im-on-the-wrapper',
                                outerClass: 'book-it-btn'
                            }"
                            type="form"
                            @submit="processForm"
                        >
                            <DatePicker
                                id="eventDate"
                                :label="t('widget.form.event-date')"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.event-date') })}`
                                }"
                                :min-date="
                                    DateTime.dayjsObject().format(ConstantsDateTime.dateFormatCalendar)
                                "
                                name="eventDate"
                                validation="required"
                            />
                            <NumberInput
                                id="guestAmount"
                                name="guestAmount"
                                :placeholder="`${t('widget.form.guest-amount')}*`"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.guest-amount') })}`,
                                    min: `${t('widget.form.validations.min', { field: t('widget.form.guest-amount') })}`
                                }"
                                validation="number|required|min:1"
                                secondary
                            />
                            <div class="form-section-hrz">
                                <TextInput
                                    id="name"
                                    :placeholder="`${t('widget.form.your-name')}*`"
                                    :validation-messages="{
                                        required: `${t('widget.form.validations.required', { field: t('widget.form.your-name') })}`
                                    }"
                                    name="name"
                                    validation="required"
                                />
                                <TextInput
                                    id="surname"
                                    :placeholder="`${t('widget.form.your-surname')}*`"
                                    :validation-messages="{
                                        required: `${t('widget.form.validations.required', { field: t('widget.form.your-surname') })}`
                                    }"
                                    name="surname"
                                    validation="required"
                                />
                            </div>
                            <TextInput
                                id="companyName"
                                :placeholder="t('widget.form.company-name')"
                                name="companyName"
                            />
                            <Email
                                id="email"
                                :placeholder="`${t('widget.form.email')}*`"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.email') })}`
                                }"
                                name="email"
                                validation="required"
                            />
                            <PhoneNumber
                                id="phoneNumber"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.phone-number') })}`
                                }"
                                name="phoneNumber"
                                prefix-name="phoneCountryPrefix"
                                validation="required"
                                placeholder="0 000 000 00*"
                            />
                            <Textarea
                                id="message"
                                :placeholder="t('widget.form.description-2')"
                                name="message"
                                auto-height
                            />
                            <FormKitMessages />
                        </FormKit>
                    </FormKitRoot>
                </FormKitProvider>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
// eslint-disable-next-line vue-scoped-css/require-selector-used-inside

.package-form {
    &__back-btn {
        line-height: 1;
        margin-block: var(--spacing-04);
        margin-inline: 0;
        padding-block: 0;
        padding-inline: var(--widget-inner-gutter);
    }

    &__form {
        margin-block: 48px 0;
        margin-inline: 16px;
        max-inline-size: 488px;
        padding-block-end: var(--spacing-14);

        &-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-block-end: 48px;
        }

        &-title {
            font-weight: lighter;
            margin-block-end: 16px;;
            text-align: center;
        }

        &-description {
            font-size: var( --font-medium);
            padding-inline: var(--spacing-07);
            text-align: center;
            text-wrap: balance;
        }

        .formkit-form > div {
            margin-block-end: var(--spacing-2x);
        }

        &-body {
            .form-section-hrz {
                > div:first-child {
                    margin-block-end: var(--spacing-2x);
                }

                @include min-width(md) {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;
                    gap: var(--spacing-03);

                    > div {
                        flex-grow: 1;

                        &:first-child {
                            margin-block-end: 0;
                        }
                    }
                }
            }

            :deep(.formkit-actions) {
                position: absolute;
                z-index: 100;

                .book-it-btn {
                    position: fixed;
                    z-index: 100;
                    inline-size: calc(100% - var(--size-16));
                    inset-block-end: var(--spacing-05);
                    inset-inline-end: 50%;
                    transform: translateX(50%);

                    @include min-width(md) {
                        inline-size: auto;
                    }

                    @include min-width(lg) {
                        inset-inline-end: var(--spacing-07);
                        transform: initial;
                    }
                }

                .book-it-btn button {
                    color: var(--text-primary-invert);
                    font-size: var(--font-medium);
                    font-weight: var(--font-weight-medium);
                    inline-size: 100%;
                    letter-spacing: var(--letter-spacing-0);
                    line-height: var(--line-height-150);
                }
            }

            @include min-width(md) {
                padding-block-end: 0;
            }
        }

        @include min-width(md) {
            margin-inline: auto;
        }
    }
}
</style>
